import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowsClockwise, Megaphone, EnvelopeSimple, ChatText, WhatsappLogo, GoogleLogo, ShareNetwork, Toolbox, Lightbulb } from "@phosphor-icons/react";
import { api } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { useAppData, AreaCardShell, KpiChip, SpecLink } from "../components/AppDataParts";

const sum = (arr, fn) => arr.reduce((t, x) => t + (fn(x) || 0), 0);

const CHANNELS = [
  { key: "iniziative", label: "Iniziative", icon: Lightbulb, color: "text-cyan-300", border: "hover:border-cyan-400/50", to: "/marketing/iniziative", active: true,
    desc: "Le iniziative quotidiane di V.I.K.I. per ogni sito: sintesi, stato e risultati" },
  { key: "email", label: "Email", icon: EnvelopeSimple, color: "text-amber-400", border: "hover:border-amber-400/50", to: "/marketing/email", active: true,
    desc: "Newsletter, template e liste contatti" },
  { key: "sms", label: "Sms", icon: ChatText, color: "text-sky-400", border: "", active: false,
    desc: "Campagne SMS ai tuoi contatti" },
  { key: "whatsapp", label: "WhatsApp", icon: WhatsappLogo, color: "text-emerald-400", border: "hover:border-emerald-400/50", to: "/marketing/whatsapp", active: true,
    desc: "Messaggi WhatsApp ai tuoi contatti" },
  { key: "googleads", label: "Google Ads", icon: GoogleLogo, color: "text-blue-400", border: "hover:border-blue-400/50", to: "/marketing/googleads", active: true,
    desc: "Campagne pubblicitarie Google Ads: stato, budget e confronto con il SEO" },
  { key: "social", label: "Social", icon: ShareNetwork, color: "text-fuchsia-400", border: "hover:border-fuchsia-400/50", to: "/marketing/social", active: true,
    desc: "Campagne e contenuti su Facebook, Instagram e TikTok" },
  { key: "strumenti", label: "Strumenti", icon: Toolbox, color: "text-emerald-400", border: "hover:border-emerald-400/50", to: "/marketing/strumenti", active: true,
    desc: "Link UTM tracciati, codici QR e anteprima risultati Google" },
];

const CampaignsTable = ({ campaigns }) => (
  <div className="overflow-x-auto">
    <table className="w-full text-xs" data-testid="campaigns-table">
      <thead>
        <tr className="text-[10px] uppercase tracking-wider text-white/30 text-left">
          <th className="py-1.5 pr-2 font-medium">Campagna</th>
          <th className="py-1.5 pr-2 font-medium">Canale</th>
          <th className="py-1.5 pr-2 font-medium">Stato</th>
          <th className="py-1.5 pr-2 font-medium text-right">Inviate</th>
          <th className="py-1.5 pr-2 font-medium text-right">Aperte</th>
          <th className="py-1.5 font-medium text-right">Clic</th>
        </tr>
      </thead>
      <tbody>
        {campaigns.slice(0, 8).map((c, i) => (
          <tr key={`${c.name}-${i}`} className="border-t border-white/5 text-white/70">
            <td className="py-1.5 pr-2 truncate max-w-[140px]" title={c.name}>{c.name}</td>
            <td className="py-1.5 pr-2 text-white/45">{c.channel || "—"}</td>
            <td className="py-1.5 pr-2">
              <span className={`text-[10px] font-semibold ${c.status === "attiva" ? "text-emerald-400" : "text-white/40"}`}>{c.status || "—"}</span>
            </td>
            <td className="py-1.5 pr-2 text-right">{c.sent ?? "—"}</td>
            <td className="py-1.5 pr-2 text-right">{c.opened ?? "—"}</td>
            <td className="py-1.5 text-right">{c.clicked ?? "—"}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

export default function MarketingPage() {
  const { data, refreshing, refresh } = useAppData("/admin/apps/marketing");
  const [adsCfg, setAdsCfg] = useState(null);
  const [waCfg, setWaCfg] = useState(null);
  useEffect(() => {
    api.get("/admin/ads/config").then(({ data: c }) => setAdsCfg(c)).catch(() => {});
    api.get("/admin/whatsapp/config").then(({ data: c }) => setWaCfg(c)).catch(() => {});
  }, []);
  const areas = data?.areas || [];
  const connected = areas.filter((a) => a.status === "connected" && a.data);
  const allCampaigns = connected.flatMap((a) => a.data.campaigns || []);
  const totals = [
    { key: "campagne", label: "Campagne", value: allCampaigns.length },
    { key: "inviate", label: "Inviate", value: sum(allCampaigns, (c) => c.sent) },
    { key: "aperte", label: "Aperte", value: sum(allCampaigns, (c) => c.opened) },
    { key: "clic", label: "Clic", value: sum(allCampaigns, (c) => c.clicked) },
    { key: "contatti", label: "Contatti", value: sum(connected, (a) => a.data.contacts?.total) },
  ];

  return (
    <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8 sm:py-12" data-testid="marketing-page">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-10 animate-fade-up">
        <div>
          <p className="text-xs tracking-[0.3em] uppercase text-white/40 mb-2">Comunicazione e campagne</p>
          <h1 className="font-heading text-4xl sm:text-5xl font-bold tracking-tighter leading-none">Marketing</h1>
          <p className="text-sm text-slate-400 mt-3 max-w-2xl">
            Scegli il canale con cui comunicare con i tuoi contatti. Le liste sono centralizzate e saranno disponibili per tutti i canali.
          </p>
        </div>
        <Button data-testid="apps-refresh-button" onClick={refresh} disabled={refreshing}
          variant="ghost" className="h-10 px-5 border border-white/20 text-white hover:bg-white/5 hover:text-white rounded-full text-xs font-semibold">
          <ArrowsClockwise size={15} className={`mr-1.5 ${refreshing ? "animate-spin" : ""}`} /> {refreshing ? "Aggiornamento..." : "Aggiorna ora"}
        </Button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 mb-12">
        {CHANNELS.map((ch, i) => {
          const Icon = ch.icon;
          const inner = (
            <>
              <Icon size={44} weight="duotone" className={`${ch.color} mb-5 ${ch.active ? "transition-transform duration-200 group-hover:scale-110" : "opacity-50"}`} />
              <h2 className={`font-heading text-2xl font-semibold tracking-tight mb-1 ${ch.active ? "" : "text-white/50"}`}>{ch.label}</h2>
              <p className={`text-sm mb-4 ${ch.active ? "text-slate-400" : "text-white/25"}`}>{ch.desc}</p>
              {ch.active ? (
                <span className="inline-flex items-center gap-3">
                  {ch.key === "googleads" && adsCfg && (adsCfg.configured ? (
                    <span data-testid="ads-status-badge" className="inline-flex items-center h-6 px-3 rounded-full bg-emerald-400/10 border border-emerald-400/30 text-[10px] font-semibold uppercase tracking-wider text-emerald-400">Attivo</span>
                  ) : (
                    <span data-testid="ads-status-badge" className="inline-flex items-center h-6 px-3 rounded-full bg-amber-400/10 border border-amber-400/30 text-[10px] font-semibold uppercase tracking-wider text-amber-300">Non ancora attivo</span>
                  ))}
                  {ch.key === "whatsapp" && waCfg && (waCfg.configured ? (
                    <span data-testid="whatsapp-channel-badge" className="inline-flex items-center h-6 px-3 rounded-full bg-emerald-400/10 border border-emerald-400/30 text-[10px] font-semibold uppercase tracking-wider text-emerald-400">Attivo</span>
                  ) : (
                    <span data-testid="whatsapp-channel-badge" className="inline-flex items-center h-6 px-3 rounded-full bg-amber-400/10 border border-amber-400/30 text-[10px] font-semibold uppercase tracking-wider text-amber-300">Non ancora attivo</span>
                  ))}
                  <span className="text-xs font-semibold text-amber-300">Apri →</span>
                </span>
              ) : (
                <span className="inline-flex items-center h-6 px-3 rounded-full bg-white/5 border border-white/10 text-[10px] font-semibold uppercase tracking-wider text-white/40">
                  In arrivo
                </span>
              )}
            </>
          );
          const cls = `group text-left p-8 rounded-lg bg-surface border border-surface-border animate-fade-up ${ch.active ? `${ch.border} transition-colors duration-200 cursor-pointer` : "cursor-default"}`;
          return ch.active ? (
            <Link key={ch.key} to={ch.to} data-testid={`marketing-channel-${ch.key}`} className={cls} style={{ animationDelay: `${80 + i * 80}ms` }}>
              {inner}
            </Link>
          ) : (
            <div key={ch.key} data-testid={`marketing-channel-${ch.key}`} className={cls} style={{ animationDelay: `${80 + i * 80}ms` }}>
              {inner}
            </div>
          );
        })}
      </div>

      <p className="text-xs tracking-[0.3em] uppercase text-white/40 mb-4">Dati dalle webapp collegate ({connected.length}/{areas.length})</p>

      {connected.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-8 animate-fade-up" data-testid="marketing-totals">
          {totals.map((t) => <KpiChip key={t.key} kpi={t} />)}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-4">
        {areas.map((a) => (
          <AreaCardShell key={a.area} area={a.area} status={a.status} error={a.error}
            updatedAt={a.updated_at} testId={`marketing-card-${a.area}`} endpointHint="/api/paq/marketing">
            <div className="space-y-3">
              {(a.data?.kpis || []).length > 0 && (
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {(a.data.kpis || []).slice(0, 3).map((k) => <KpiChip key={k.key || k.label} kpi={k} />)}
                </div>
              )}
              {(a.data?.campaigns || []).length > 0 ? (
                <CampaignsTable campaigns={a.data.campaigns} />
              ) : (
                <p className="text-xs text-white/40 flex items-center gap-1.5">
                  <Megaphone size={13} className="text-white/30" /> Nessuna campagna esposta.
                </p>
              )}
              {a.data?.contacts?.total !== undefined && (
                <p className="text-[11px] text-white/45">
                  Contatti: <span className="text-white font-semibold">{a.data.contacts.total}</span>
                  {a.data.contacts.new_30d !== undefined && <> — nuovi ultimi 30gg: <span className="text-emerald-400 font-semibold">{a.data.contacts.new_30d}</span></>}
                </p>
              )}
            </div>
          </AreaCardShell>
        ))}
      </div>

      <SpecLink />
    </main>
  );
}
